import { useCallback, useEffect, useMemo } from "react";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import Modal from "../../components/common/Modal";
import { IDepartment, IRoute, IStaff } from "../../utils/types";
import TextInput from "../../components/common/TextInput";
import Button from "../../components/common/button";
import Select from "../../components/common/Select";
import Switch from "../../components/common/Switch";
import { AddStaffSvg, RemoveStaffSvg } from "../../icons";
import useAddStaff from "../../hooks/use-add-staff";
import { errorToast, successToast } from "../../utils/helpers";
import { queryClient } from "../../App";
import Spinner from "../../components/common/Spinner";
import useUpdateStaff from "../../hooks/use-update-staff";

const ROUTE_ACCESS_OPTIONS = [
  { label: "Read", value: "read" },
  { label: "Write", value: "write" },
];

type DepartmentOption = {
  label: string;
  value: string;
  routes?: IRoute[];
};

type RouteAccess = {
  enabled: boolean;
  access: {
    read: boolean;
    write: boolean;
  };
};

type DepartmentForm = {
  department: DepartmentOption | null;
  routes: Record<string, RouteAccess>;
  selectAll: boolean;
  allWrite: boolean;
  accessLevel: "app" | "route";
};

type AddStaffFormValues = {
  name: string;
  email: string;
  password: string;
  departments: DepartmentForm[];
};

type AddStaffModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  departmentsData: IDepartment[];
  isEdit?: boolean;
  staffData?: IStaff | null;
};

const StaffModal = ({
  isOpen,
  closeModal,
  departmentsData,
  isEdit = false,
  staffData = null,
}: AddStaffModalProps) => {
  const { mutate: addStaffUser, isPending: loading } = useAddStaff();
  const { mutate: updateStaffUser, isPending: updateLoading } = useUpdateStaff(
    staffData?._id
  );
  const methods = useForm<AddStaffFormValues>({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      departments: [
        {
          department: null,
          routes: {},
          selectAll: false,
          allWrite: false,
          accessLevel: "app",
        },
      ],
    },
    mode: "onChange"
  });

  const { reset, watch, control, setValue, handleSubmit, register, formState: { errors } } = methods;

  const getMappedStaffData = useCallback(() => {
    if (!staffData) {
      return {
        name: "",
        email: "",
        password: "",
        departments: [
          {
            department: null,
            routes: {},
            selectAll: false,
            allWrite: false,
            accessLevel: "app",
          },
        ],
      };
    }
    const mappedDepartments = staffData.department?.map((dept, index) => {
      const findDepartment = departmentOptions.find(
        (item) => item.value === dept._id
      );
      if (!findDepartment) return;

      const newRoutes: Record<string, RouteAccess> =
        findDepartment.routes?.reduce((acc, route) => {
          const findRoutes = dept.accessPages?.find(
            (item) => item.name === route.value
          );
          acc[route.value] = findRoutes
            ? {
                enabled: findRoutes.read,
                access: { read: findRoutes.read, write: findRoutes.write },
              }
            : {
                enabled: false,
                access: { read: false, write: false },
              };
          return acc;
        }, {} as Record<string, RouteAccess>) ?? {};

      if (index >= 1) {
        append({
          department: null,
          routes: {},
          selectAll: false,
          allWrite: false,
          accessLevel: "app",
        });
      }
      const enabledRoutes = Object.values(newRoutes).filter(route => route.enabled);
      const allWrite = enabledRoutes.length > 0 && enabledRoutes.every(route => route.access.write);
      return {
        department: findDepartment,
        routes: newRoutes,
        selectAll: dept.accessPages?.length === findDepartment.routes?.length,
        allWrite,
        accessLevel: Object.keys(newRoutes).length ? "route" : "app",
      };
    });
    return {
      name: staffData.name,
      email: staffData.email,
      password: "******",
      departments: mappedDepartments,
    };
  }, [staffData, departmentsData]);

  // Reset form when modal opens in edit mode and staffData changes
  useEffect(() => {
    if (isOpen && isEdit && staffData) {
      reset(getMappedStaffData() as AddStaffFormValues);
    } else if (!isEdit) {
      reset({
        name: "",
        email: "",
        password: "",
        departments: [
          { department: null, routes: {}, selectAll: false, allWrite: false },
        ],
      });
    }
  }, [isOpen, isEdit, staffData, reset, departmentsData, getMappedStaffData]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "departments",
  });

  const departments = watch("departments") || [];
  const departmentOptions: DepartmentOption[] = useMemo(
    () =>
      departmentsData.map((dept) => ({
        label: dept.departmentName,
        value: dept._id,
        routes: dept.routes,
      })),
    [departmentsData]
  );

  // Register validation for departments
  useEffect(() => {
    departments.forEach((_, index) => {
      register(`departments.${index}`, {
        validate: (value) => {
          if (!value.department) {
            return;
          }
          if (value.department.routes?.length) {
            const enabledRoutes = value.department.routes?.filter(route => value.routes[route.value]?.enabled) ?? [];
            if (enabledRoutes.length === 0) {
              return "Please select at least one access page for this department";
            }
          }
          return true;
        }
      });
    });
  }, [departments, register]);

  const handleDepartmentChange = (
    index: number,
    option: DepartmentOption | null
  ) => {
    if (!option) return;

    const newRoutes: Record<string, RouteAccess> =
      option.routes?.reduce((acc, route) => {
        acc[route.value] = {
          enabled: false,
          access: { read: false, write: false },
        };
        return acc;
      }, {} as Record<string, RouteAccess>) ?? {};

    setValue(`departments.${index}`, {
      department: option,
      routes: newRoutes,
      selectAll: false,
      allWrite: false,
      accessLevel: Object.keys(newRoutes).length ? "route" : "app",
    });
  };

  const handleSelectAll = (index: number, isChecked: boolean) => {
    const department = departments[index];
    if (!department.department?.routes) return;

    const newRoutes: Record<string, RouteAccess> = { ...department.routes };
    department.department.routes.forEach((route) => {
      newRoutes[route.value] = {
        enabled: isChecked,
        access: {
          read: isChecked,
          write: isChecked ? department.allWrite : false,
        },
      };
    });

    setValue(`departments.${index}.routes`, newRoutes);
    setValue(`departments.${index}.selectAll`, isChecked);

    if (!isChecked) {
      // If turning off selectAll, also turn off allWrite
      setValue(`departments.${index}.allWrite`, false);
    }
  };

  const handleAllWrite = (index: number, isChecked: boolean) => {
    const department = departments[index];
    if (!department.department?.routes) return;

    const newRoutes = { ...department.routes };
    department.department.routes.forEach((route) => {
      const current = newRoutes[route.value];
      if (current?.enabled) {
        newRoutes[route.value].access.write = isChecked;
      }
    });

    setValue(`departments.${index}.routes`, newRoutes);
    setValue(`departments.${index}.allWrite`, isChecked);
  };

  const handleRouteToggle = (
    index: number,
    value: string,
    currentlyChecked: boolean
  ) => {
    const department = departments[index];
    const newRoutes = { ...department.routes };
    newRoutes[value].enabled = !currentlyChecked;
    newRoutes[value].access = !currentlyChecked
      ? {
          read: true,
          write: department.allWrite,
        }
      : {
          read: false,
          write: false,
        };

    setValue(`departments.${index}.routes`, newRoutes);

    // Check if all routes are enabled now
    const allEnabled = department.department?.routes?.every(
      (route) => newRoutes[route.value]?.enabled
    );

    setValue(`departments.${index}.selectAll`, !!allEnabled);

    // Check if we need to turn off allWrite
    const hasEnabledRoutes = department.department?.routes?.some(
      route => newRoutes[route.value]?.enabled && newRoutes[route.value]?.access.read
    );
    if (!hasEnabledRoutes) {
      setValue(`departments.${index}.allWrite`, false);
    }
  };

  const generatePassword = () => {
    const randomPass = Math.random().toString(36).slice(-8);
    setValue("password", randomPass);
  };

  const handleDiscard = () => {
    reset();
  };

  const onSubmit = (data: AddStaffFormValues) => {
    const payload = {
      name: data.name,
      ...(isEdit ? {} : { email: data.email, password: data.password }),
      department: data.departments.map((dept) => ({
        _id: dept.department?.value,
        departmentName: dept.department?.label,
        accessLevel: dept.accessLevel,
        accessPages:
          dept.department?.routes
            ?.filter((route) => dept.routes[route.value]?.enabled)
            .map((route) => ({
              name: route.value,
              ...dept.routes[route.value].access,
            })) ?? [],
      })),
    };

    if (isEdit) {
      updateStaffUser(payload, {
        onSuccess: async (res) => {
          reset();
          closeModal();
          successToast(res.result.message);
          await queryClient.refetchQueries({
            queryKey: ["staff"],
          });
        },
        onError: (err) => {
          errorToast(err?.response?.data?.result?.message);
        },
      });
    } else {
      addStaffUser(payload, {
        onSuccess: async (res) => {
          reset();
          closeModal();
          successToast(res.result.message);
          await queryClient.refetchQueries({
            queryKey: ["staff"],
          });
        },
        onError: (err) => {
          errorToast(err?.response?.data?.result?.message);
        },
      });
    }
  };

  const availableDepartmentOptions = (index: number) =>
    departmentOptions.filter(
      (option) =>
        !departments.some(
          (dept, i) => i !== index && dept.department?.value === option.value
        )
    );

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      title={`${isEdit ? "Edit" : "Add"} Staff`}
      className="w-[760px] max-h-[90vh]"
    >
      <FormProvider {...methods}>
        {loading && <Spinner fullScreenOverlay className="z-[100]" />}
        {updateLoading && <Spinner fullScreenOverlay className="z-[100]" />}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col h-full  overflow-hidden"
        >
          {/* Scrollable content */}
          <div
            className="overflow-y-auto p-3 pr-4 space-y-4 pb-15"
            style={{ maxHeight: "70vh" }}
            id="modal_custom"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <TextInput
                  name="name"
                  label="Name"
                  placeholder="Enter Name"
                  rules={{ required: "Name is required" }}
                />
              </div>
              {/* Email */}
              <div>
                <TextInput
                  name="email"
                  label="Email"
                  placeholder="Enter Email"
                  type="email"
                  rules={{ required: "Email is required" }}
                  disabled={isEdit}
                />
              </div>
              {/* Password */}
              <div className="min-h-[80px]">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <TextInput
                      name="password"
                      label="Password"
                      placeholder="Enter Password"
                      type="text"
                      value={watch("password")}
                      rules={{ required: "Password is required" }}
                      disabled={isEdit}
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={generatePassword}
                    className="text-xs mt-[22px]"
                    disabled={isEdit}
                  >
                    Generate
                  </Button>
                </div>
              </div>

              {/* Department Select */}
              <div className="col-span-2 space-y-4">
                {fields.map((field, index) => (
                  <div key={field.id} className="space-y-4 relative group">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-h-[80px]">
                        <Select
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 99,
                            }),
                            menu: (base) => ({ ...base, zIndex: 99 }),
                          }}
                          name={`departments.${index}.department`}
                          label={`Department`}
                          placeholder="Select Department"
                          options={availableDepartmentOptions(index)}
                          value={departments?.[index]?.department}
                          onChange={(option: DepartmentOption) =>
                            handleDepartmentChange(index, option)
                          }
                          rules={{ required: "Department is required" }}
                        />
                        {errors.departments?.[index] && errors.departments[index].message !== "Please select at least one access page for this department" && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.departments[index].message}
                          </p>
                        )}
                      </div>
                      {index !== 0 ? (
                        <RemoveStaffSvg
                          onClick={() => remove(index)}
                          className="mt-6 cursor-pointer"
                        />
                      ) : (
                        <AddStaffSvg
                          onClick={() =>
                            append({
                              department: null,
                              routes: {},
                              accessLevel: "app",
                              selectAll: false,
                              allWrite: false,
                            })
                          }
                          disabled={!departments?.[index]?.department}
                          className={`mt-6 ${
                            !departments?.[index]?.department
                              ? "cursor-default"
                              : "cursor-pointer"
                          }`}
                        />
                      )}
                    </div>

                    {/* Department's Routes */}
                    {departments?.[index]?.department?.routes?.length ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-base text-[#666672]">
                              Select All
                            </span>
                            <Switch
                              name={`selectAll-${departments?.[index]?.department.value}`}
                              checked={departments?.[index]?.selectAll}
                              onCheck={() =>
                                handleSelectAll(
                                  index,
                                  !departments?.[index]?.selectAll
                                )
                              }
                            />
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <span className="text-base text-[#666672]">
                                All Write
                              </span>
                              <Switch
                                name={`allWrite-${departments?.[index]?.department.value}`}
                                onCheck={() =>
                                  handleAllWrite(
                                    index,
                                    !departments?.[index]?.allWrite
                                  )
                                }
                                checked={departments?.[index]?.allWrite}
                                disabled={
                                  !departments?.[
                                    index
                                  ].department?.routes?.some(
                                    (route) =>
                                      departments?.[index]?.routes[route.value]
                                        ?.enabled
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#FAFAFA] p-4 rounded-md">
                          <div className="grid gap-3">
                            {departments?.[index]?.department?.routes?.map(
                              (route) => (
                                <div
                                  key={route._id}
                                  className="flex items-center justify-between"
                                >
                                  <span className="text-base text-[#666672] w-1/2 truncate">
                                    {route.name}
                                  </span>
                                  <div className="flex items-center gap-2 w-1/2 justify-end">
                                    <Switch
                                      name={route.value}
                                      checked={
                                        departments?.[index]?.routes[
                                          route.value
                                        ]?.enabled
                                      }
                                      onCheck={() =>
                                        handleRouteToggle(
                                          index,
                                          route.value,
                                          departments?.[index]?.routes[
                                            route.value
                                          ]?.enabled
                                        )
                                      }
                                    />
                                    <div className="w-[226px]">
                                      <Select
                                        isMulti
                                        options={ROUTE_ACCESS_OPTIONS}
                                        value={ROUTE_ACCESS_OPTIONS.filter(
                                          (opt) =>
                                            departments?.[index]?.routes[
                                              route.value
                                            ]?.access[
                                              opt.value as "read" | "write"
                                            ]
                                        )}
                                        onChange={(
                                          value: {
                                            label: string;
                                            value: "read" | "write";
                                          }[]
                                        ) => {
                                          const access = {
                                            read: false,
                                            write: false,
                                          };

                                          for (const item of value) {
                                            access[item.value] = true;
                                          }

                                          const enabled = access.read;
                                          const updatedRoutes = {
                                            ...departments?.[index]?.routes,
                                            [route.value]: {
                                              enabled,
                                              access: enabled
                                                ? access
                                                : { read: false, write: false },
                                            },
                                          };

                                          const departmentRoutes =
                                            departments?.[index]?.department
                                              ?.routes || [];

                                          let allEnabled = true;
                                          let allEnabledHaveWrite = true;
                                          let hasEnabledRoutes = false;

                                          for (const r of departmentRoutes) {
                                            const routeData =
                                              r.value === route.value
                                                ? updatedRoutes[r.value]
                                                : departments?.[index]?.routes[
                                                    r.value
                                                  ];

                                            if (!routeData?.enabled) {
                                              allEnabled = false;
                                            } else {
                                              hasEnabledRoutes = true;
                                              if (!routeData.access.write) {
                                                allEnabledHaveWrite = false;
                                              }
                                            }

                                            if (
                                              !allEnabled &&
                                              !allEnabledHaveWrite
                                            )
                                              break;
                                          }
                                          
                                          if (!hasEnabledRoutes) {
                                            allEnabledHaveWrite = false;
                                          }

                                          setValue(
                                            `departments.${index}.routes`,
                                            updatedRoutes
                                          );
                                          setValue(
                                            `departments.${index}.selectAll`,
                                            allEnabled
                                          );
                                          setValue(
                                            `departments.${index}.allWrite`,
                                            allEnabledHaveWrite
                                          );
                                        }}
                                        className="w-full"
                                        isDisabled={
                                          !departments?.[index]?.routes[
                                            route.value
                                          ]?.enabled
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                        {errors.departments?.[index]?.message === "Please select at least one access page for this department" && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.departments[index].message}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-span-2 flex justify-end gap-4 mt-4">
            <Button type="button" onClick={handleDiscard}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default StaffModal;
